import { useState, useContext, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Editor } from '@tinymce/tinymce-react';
import AuthContext from '../context/AuthContext';
import config from '../config';

const EditPost = () => {
    const [title, setTitle] = useState('');
    const [slug, setSlug] = useState('');
    const [content, setContent] = useState('');
    const [pgnData, setPgnData] = useState([]);
    const [error, setError] = useState('');
    const [uploading, setUploading] = useState(false);
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();
    const { slug: existingSlug } = useParams(); // Note: Route param might be :slug or :id depending on route config
    // Actually Home.js uses `navigate('/edit-post/' + blog.slug)` but backend uses `router.put('/:id')`.
    // I need to fetch by slug first to get the ID, or use ID in URL.
    // Let's modify Home.js to pass ID instead of slug for editing if possible, or fetch by slug then update by ID.
    // Checking Home.js handleEdit: `navigate('/edit-post/' + blog.slug)`
    // It's using slug. So I'll fetch by slug first.

    const editorRef = useRef(null);
    const [blogId, setBlogId] = useState(null);

    useEffect(() => {
        const fetchPost = async () => {
            try {
                // Fetch by slug
                const res = await axios.get(`${config.API_URL}/blogs/${existingSlug}`);
                const blog = res.data;
                setTitle(blog.title);
                setSlug(blog.slug);
                setContent(blog.content);
                setPgnData(blog.pgn_data || []);
                setBlogId(blog.id);
            } catch (err) {
                console.error("Error fetching post", err);
                setError("Failed to load blog post");
            }
        };
        fetchPost();
    }, [existingSlug]);

    const handleTitleChange = (e) => {
        const newTitle = e.target.value;
        setTitle(newTitle);
        // Only auto-update slug if it matches the generated version of old title?
        // Or just let user edit it manually. Let's not auto-update slug on edit to avoid breaking links unless intentional.
    };

    const handlePgnUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);

        // Read PGN file client-side using FileReader
        const reader = new FileReader();

        reader.onload = (event) => {
            const pgnContent = event.target.result;
            const newPgnData = [...pgnData, pgnContent];
            setPgnData(newPgnData);
            setUploading(false);

            // Auto-insert PGN placeholder at cursor
            if (editorRef.current) {
                const pgnIndex = newPgnData.length - 1;
                editorRef.current.insertContent(`<p>[PGN:${pgnIndex}]</p>`);
            }
        };

        reader.onerror = () => {
            setError('Failed to read PGN file');
            setUploading(false);
        };

        reader.readAsText(file);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!blogId) {
            setError("Missing Blog ID");
            return;
        }

        try {
            const token = localStorage.getItem('token');
            await axios.put(`${config.API_URL}/blogs/${blogId}`,
                { title, slug, content, pgn_data: pgnData },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            navigate(`/post/${slug}`); // Navigate to the (potentially new) slug
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update post');
        }
    };

    return (
        <div className="container" style={{ paddingTop: '2rem' }}>
            <div className="form-container">
                <h1>Edit Post</h1>
                {error && <div className="error">{error}</div>}
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Title</label>
                        <input
                            type="text"
                            value={title}
                            onChange={handleTitleChange}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Slug</label>
                        <input
                            type="text"
                            value={slug}
                            onChange={(e) => setSlug(e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>Upload PGN File (Optional)</label>
                        <input
                            type="file"
                            accept=".pgn"
                            onChange={handlePgnUpload}
                            disabled={uploading}
                        />
                        {uploading && <p>Uploading...</p>}
                        {pgnData.length > 0 && (
                            <p style={{ color: '#4CAF50' }}>
                                ✓ {pgnData.length} PGN file(s) uploaded
                            </p>
                        )}
                    </div>
                    <div className="form-group">
                        <label>Content</label>
                        <Editor
                            apiKey='sp05u5nayc70fuope6p4gxfnd0jqcdlm46jds60xdc38sv9r'
                            onInit={(evt, editor) => editorRef.current = editor}
                            value={content}
                            onEditorChange={(newContent) => setContent(newContent)}
                            init={{
                                height: 500,
                                menubar: false,
                                plugins: [
                                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',
                                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                    'insertdatetime', 'media', 'table', 'preview', 'help', 'wordcount'
                                ],
                                toolbar: 'undo redo | blocks | ' +
                                    'bold italic forecolor | alignleft aligncenter ' +
                                    'alignright alignjustify | bullist numlist outdent indent | ' +
                                    'removeformat | help',
                                content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                                paste_data_images: true,
                                images_upload_handler: async (blobInfo, progress) => {
                                    try {
                                        const formData = new FormData();
                                        formData.append('image', blobInfo.blob(), blobInfo.filename());

                                        const token = localStorage.getItem('token');
                                        const response = await axios.post(`${config.API_URL}/upload/image`, formData, {
                                            headers: {
                                                'Content-Type': 'multipart/form-data',
                                                Authorization: `Bearer ${token}`
                                            },
                                            onUploadProgress: (e) => {
                                                progress(e.loaded / e.total * 100);
                                            }
                                        });

                                        return response.data.url;
                                    } catch (error) {
                                        console.error('Image upload failed:', error);
                                        throw new Error('Image upload failed: ' + (error.response?.data?.message || error.message));
                                    }
                                }
                            }}
                        />
                    </div>
                    <button type="submit" className="btn">Update Post</button>
                </form>
            </div>
        </div>
    );
};

export default EditPost;
